import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy,ChangeDetectorRef,Component,inject,OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatExpansionModule } from '@angular/material/expansion';
import { JobHistoryDetailComponent } from '../job-history-detail/job-history-detail.component';
import { DynamicFormArray, FIELD_TYPES, Props } from '../../model/form.model';
import { LAYOUT_TYPE } from '../../model/common.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { CommonUtils } from '../../utils/common.utils';
import { API_STATUS,ApiEnvNames,ERROR_RESPONSE,ExportConfig,SearchConfig} from '../../model/api.model';
import { BaseComponent } from '../base/base.component';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { JobHistoryDisplayTable,JobHistoryFilterSchema,JobHistoryProps,JobHistorySearchConfig,JobHistroyDataSchema,PCSupportExportSearchConfig} from '../../model/job-history.model';
import { CommonService } from '../../services/common.service';
import { FieldNames } from '../../model/form-fields.model';
import { Subject, takeUntil } from 'rxjs';
import { CSV_EXTENSION,ExcelExportService,} from '../../services/excel-export.service';
import { PaginatorService } from '../../services/paginator-service.service';
@Component({
  selector: 'app-job-history',
  standalone: true,
  imports: [ CommonModule,ReactiveFormsModule,MatCardModule,MatFormFieldModule,MatInputModule,MatExpansionModule,
    PageContainerComponent,JobHistoryDetailComponent],
  templateUrl: './job-history.component.html',
  styleUrl: './job-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobHistoryComponent extends BaseComponent implements OnDestroy {
  protected data = 'JobHistoryComponent';
  public override dataTableSchema: DataTableSchema = structuredClone(JobHistoryDisplayTable);
  public filterSchema: DynamicFormArray = structuredClone(JobHistoryFilterSchema);
  protected props: Props = JobHistoryProps;
  readonly LayoutType: typeof LAYOUT_TYPE = LAYOUT_TYPE;
  public mode: 'View' | 'List' = 'List';
  public dataSet: any = {};
  title = '';
  commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  changeRef = inject(ChangeDetectorRef);
  paginatorService = inject(PaginatorService);
  excelService = inject(ExcelExportService);
  private isFirstLoad = true;
  isDataInitialized = false;
  currentPage: any;
  public tableFilter = '';
  readonly destroy$ = new Subject<void>();

  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.updateTableCallbacks();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback;
    this.dataTableSchema.exportCallback = this.exportCallback;
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      } else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        this.onSearchClicked('');
      }
    });
  }

  override onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.resetSorting()
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig();
    this.fetchAllData(searchConfig);
  };

  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true;
    this.fetchAllData(searchConfig, true);
  };

  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig);
      }
    }
  };
  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    const fieldMapping:any = {
      'DEALER_CODE': 'dealerNumber',
      'SPIN_ID': 'spinId',
      'FIRST_NAME': 'firstName',
      'LAST_NAME': 'lastName',
      'associate_type': 'associate_type'
    }
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[fieldMapping[field['id']]] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  }

  getSearchConfig = () => {
    const searchConfig = structuredClone(JobHistorySearchConfig);
    this.filterSchema['FieldsArray'].forEach((field) => {
      if (field['selectedValue'] &&field['selectedValue'] !== null &&field['selectedValue'] !== '') {
        if (Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])) {
          searchConfig['body'][field['fieldName']] = field['selectedValue'];
        } else {
          searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
        }
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchConfig['body'][field['fieldName']] = '';
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if(this.sortBy && Object.keys(this.sortBy).length > 0 ) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig;
  };

  fetchAllData = (searchParam: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    this.changeRef.detectChanges();
    if (callFromExport) {
      // Export mode: no pagination
    } else {
      searchParam['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchParam['body'].pageSize = Number(this.currentPage?.pageSize);
    }
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['JobEnrollment']).then(async (response) => {
        if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
          if (callFromExport) {
            const exportConfig: ExportConfig = {
              tableSchema: this.dataTableSchema,
              environment: ApiEnvNames['StaffMaster'],
              fileExtension: CSV_EXTENSION,
              searchParameters: PCSupportExportSearchConfig,
              exportFileName: 'PC_Support_Status',
            };
            await this.service.downloadExcelFromS3(response.result[0], exportConfig);
          } else {
            this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
            this.dataTableSchema.dataSource = response.result;
            if (!this.isDataInitialized && response.result.length > 0) {
              this.isDataInitialized = true;
            }
          }
        } else {
          this.setTableLoadingState(false);
          this.dataTableSchema.dataSource = [];
          this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
        }
        this.setTableLoadingState(false);
        this.changeRef.detectChanges()
      })
      .catch((err) => {
        this.setTableLoadingState(false);
        this.dataTableSchema.dataSource = [];
        console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      })
      .finally(() => {
        this.setTableLoadingState(false);
      });
  };
  updateTableCallbacks() {
    this.dataTableSchema.columnSchema.forEach((col) => {
      if (col['fieldKey'] === JobHistroyDataSchema[FieldNames['SpinId']]) {
        col['onClick'] = this.columnOnClick;
      }
    });
  }
  columnOnClick = (event: any) => {
    this.dataSet = event;
    this.mode = 'View';
  };
  onDetailViewChange(_event: any) {
    this.mode = 'List';
    this.commonUtils.spinner('hide');
  }

  override onResetClicked = () => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map(
      (field) => ({ ...field, selectedValue: field['defaultValue'] })
    );
    this.dataTableSchema.inputFilter = ''
    this.resetSorting()
    this.resetDataTable();
    this.isDataInitialized = false;
  };
  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          page: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.checkForAnyEmptyFields()
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
